import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Phone, CheckCircle2, ArrowRight, Building2, Home as HomeIcon } from 'lucide-react';
import axios from 'axios';
import { API } from '../lib/api';



const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error('Error fetching services:', error);
      }
      setLoading(false);
    };
    fetchServices();
  }, []);

  const commercialServices = services.filter(s => s.category === 'commercial' || s.category === 'structural');
  const residentialServices = services.filter(s => s.category === 'residential' || s.category === 'finishing' || s.category === 'exterior');

  const ServiceCard = ({ service }) => (
    <div 
      id={service.id}
      className="service-card group"
      data-testid={`service-card-${service.id}`}
    >
      <div className="img-zoom h-56 mb-6">
        <img src={service.image} alt={service.title} className="w-full h-full object-cover" />
      </div>
      <h3 className="heading-sm text-slate-900 mb-3" style={{ fontFamily: 'Oswald, sans-serif' }}>{service.title}</h3>
      <p className="text-slate-600 mb-4">{service.description}</p>
      
      <div className="mb-6">
        <p className="text-sm font-semibold text-slate-900 mb-3 uppercase tracking-wider">Benefits:</p>
        <ul className="space-y-2">
          {service.benefits.map((benefit, index) => (
            <li key={index} className="flex items-center gap-2 text-slate-600 text-sm">
              <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
              {benefit}
            </li>
          ))}
        </ul>
      </div>

      <Link to="/contact">
        <Button className="w-full btn-accent" data-testid={`service-quote-btn-${service.id}`}>
          Get a Quote
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </Link>
    </div>
  );

  return (
    <div data-testid="services-page">
      <Helmet>
        <title>Construction Services | Framing, Drywall, Painting, Siding | S&S Group Services Vernon BC</title>
        <meta name="description" content="Professional construction services in Vernon & BC: framing, drywall, painting, siding, flooring, interior finishing, commercial & residential builds. Licensed & insured. Free estimates." />
        <meta name="keywords" content="framing contractor Vernon BC, drywall installation BC, painting services Vernon, siding contractor BC, flooring installation, interior finishing, commercial construction BC, residential construction Vernon" />
        <link rel="canonical" href="https://www.ssgroupservices.ca/services" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.ssgroupservices.ca/services" />
        <meta property="og:title" content="Construction Services | S&S Group Services Vernon BC" />
        <meta property="og:description" content="Framing, drywall, painting, siding, flooring & finishing across BC. Licensed & insured. Free estimates." />
        <meta property="og:image" content="https://customer-assets-v7afamib.emergentagent.net/job_05714f6e-3774-4985-88d6-8908b452b8f7/artifacts/57ch736f_IMG_8182.webp" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Construction Services | S&S Group Services Vernon BC" />
        <meta name="twitter:description" content="Framing, drywall, painting, siding, flooring & finishing across BC. Free estimates." />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.ssgroupservices.ca/" },
          { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://www.ssgroupservices.ca/services" }
        ]
      }) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": "Construction Services by S&S Group Services",
        "itemListElement": ["Framing", "Drywall & Taping", "Painting", "Siding", "Flooring", "Interior Finishing", "Commercial Construction", "Residential Construction"].map((s, i) => ({
          "@type": "Service",
          "position": i + 1,
          "name": s,
          "provider": { "@type": "GeneralContractor", "name": "S&S Group Services" },
          "areaServed": "British Columbia, Canada"
        }))
      }) }} />
      {/* Hero Section */}
      <section className="relative py-24 bg-slate-900" data-testid="services-hero">
        <div className="absolute inset-0 grid-pattern" />
        <div className="container relative z-10">
          <div className="max-w-3xl">
            <span className="caption text-blue-400">Our Expertise</span>
            <h1 className="heading-xl text-white mt-3 mb-6">Professional Construction Services</h1>
            <p className="text-slate-300 text-lg mb-8">
              From structural framing to interior finishing, we offer comprehensive construction solutions for commercial and residential projects across British Columbia.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact">
                <Button size="lg" className="btn-accent" data-testid="services-hero-quote-btn">
                  Request a Consultation
                </Button>
              </Link>
              <a href="tel:+16045551234">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-slate-900" data-testid="services-hero-call-btn">
                  <Phone className="w-4 h-4 mr-2" />
                  Call Now
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Tabs */}
      <section className="section-padding bg-white" data-testid="services-tabs-section">
        <div className="container">
          {loading ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : (
            <Tabs defaultValue="all" className="w-full">
              <div className="flex justify-center mb-12">
                <TabsList className="bg-slate-100 p-1">
                  <TabsTrigger value="all" className="px-6 py-3 font-semibold uppercase tracking-wider text-sm" style={{ fontFamily: 'Oswald, sans-serif' }} data-testid="tab-all">
                    All Services
                  </TabsTrigger>
                  <TabsTrigger value="commercial" className="px-6 py-3 font-semibold uppercase tracking-wider text-sm" style={{ fontFamily: 'Oswald, sans-serif' }} data-testid="tab-commercial">
                    <Building2 className="w-4 h-4 mr-2" />
                    Commercial
                  </TabsTrigger>
                  <TabsTrigger value="residential" className="px-6 py-3 font-semibold uppercase tracking-wider text-sm" style={{ fontFamily: 'Oswald, sans-serif' }} data-testid="tab-residential">
                    <HomeIcon className="w-4 h-4 mr-2" />
                    Residential
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="all">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {services.map((service) => (
                    <ServiceCard key={service.id} service={service} />
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="commercial">
                <div className="mb-12">
                  <h2 className="heading-md text-slate-900 mb-4">Commercial Construction Services</h2>
                  <p className="text-slate-600 max-w-2xl">
                    We specialize in commercial projects including office buildings, retail spaces, warehouses, and tenant improvements. Our team delivers projects on time and within budget.
                  </p>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {commercialServices.map((service) => (
                    <ServiceCard key={service.id} service={service} />
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="residential">
                <div className="mb-12">
                  <h2 className="heading-md text-slate-900 mb-4">Residential Construction Services</h2>
                  <p className="text-slate-600 max-w-2xl">
                    From custom home builds to renovations, we bring expertise and craftsmanship to every residential project. Your home deserves the best, and we deliver.
                  </p>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {residentialServices.map((service) => (
                    <ServiceCard key={service.id} service={service} />
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          )}
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding bg-slate-50" data-testid="process-section">
        <div className="container">
          <div className="text-center mb-16">
            <span className="caption text-blue-600">How We Work</span>
            <h2 className="heading-lg text-slate-900 mt-3">Our Process</h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Consultation', desc: 'We discuss your project needs and vision' },
              { step: '02', title: 'Planning', desc: 'Detailed scope, timeline, and budget' },
              { step: '03', title: 'Execution', desc: 'Quality construction with regular updates' },
              { step: '04', title: 'Completion', desc: 'Final walkthrough and handover' },
            ].map((item, index) => (
              <div key={index} className="text-center" data-testid={`process-step-${index}`}>
                <div className="w-16 h-16 bg-blue-600 text-white flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold" style={{ fontFamily: 'Oswald, sans-serif' }}>{item.step}</span>
                </div>
                <h3 className="heading-sm text-slate-900 mb-2" style={{ fontFamily: 'Oswald, sans-serif' }}>{item.title}</h3>
                <p className="text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-slate-900" data-testid="services-cta">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="heading-lg text-white mb-6">Ready to Get Started?</h2>
            <p className="text-slate-300 text-lg mb-8">
              Contact us today for a free consultation. Let's discuss how we can bring your construction project to life.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button size="lg" className="btn-accent" data-testid="services-cta-quote-btn">
                  Get a Free Quote
                </Button>
              </Link>
              <Link to="/gallery">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-slate-900" data-testid="services-cta-gallery-btn">
                  View Our Work
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
